import { useEffect, useMemo, useState } from 'react'
import {
  Button,
  Card,
  Empty,
  InputNumber,
  Message,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from '@arco-design/web-react'
import { getModelPricingSnapshotApi } from '@/api/modelPricing'
import type { OptionsAPI } from '../useOptions'
import { Pill } from '@/utils/render'

interface Props {
  options: OptionsAPI
}

interface ToolPriceRow {
  key: string
  perThousand: number
  perCall: number
  builtin: boolean
}

/** 后端内置默认（未配置时生效），与 setting/operation_setting/tools.go 一致 */
const BUILTIN_TOOL_PRICES: Record<string, number> = {
  web_search: 10,
  web_search_preview: 10,
  file_search: 2.5,
  google_search: 14,
  image_generation: 150,
}

const parseNumber = (v: unknown): number => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

/**
 * 两种「不在这张表里」的计费维度：
 * 1) 表达式计费（tiered_expr）的模型：价格由 billing_expr 决定，改 ModelRatio 不生效
 * 2) 内置工具调用附加费：按次计价，$ / 千次
 * 它们都会真实影响客户账单，所以必须能看见。
 */
export default function BillingExtrasPanel({ options }: Props) {
  const [exprModels, setExprModels] = useState<Array<{ model: string; expr: string }>>([])
  const raw = options.get('tool_price_setting.prices', '{}')
  const [draft, setDraft] = useState<Record<string, number>>({})
  const [saving, setSaving] = useState(false)

  // 选项刷新或保存后同步草稿
  useEffect(() => {
    try {
      const parsed = raw ? (JSON.parse(raw) as Record<string, unknown>) : {}
      const next: Record<string, number> = {}
      Object.entries(parsed ?? {}).forEach(([k, v]) => {
        next[k] = parseNumber(v)
      })
      setDraft(next)
    } catch {
      setDraft({})
    }
  }, [raw])

  useEffect(() => {
    getModelPricingSnapshotApi()
      .then((snap) => {
        const options = snap.options ?? {}
        const parseMap = (raw?: string): Record<string, string> => {
          if (!raw) return {}
          try {
            const parsed = JSON.parse(raw)
            return parsed && typeof parsed === 'object' ? (parsed as Record<string, string>) : {}
          } catch {
            return {}
          }
        }
        const modes = parseMap(options['billing_setting.billing_mode'])
        const exprs = parseMap(options['billing_setting.billing_expr'])
        const list = Object.entries(modes)
          .filter(([, mode]) => mode === 'tiered_expr')
          .map(([model]) => ({ model, expr: exprs[model] ?? '' }))
          .sort((a, b) => a.model.localeCompare(b.model))
        setExprModels(list)
      })
      .catch(() => setExprModels([]))
  }, [])

  const toolRows = useMemo<ToolPriceRow[]>(() => {
    const keys = new Set([...Object.keys(BUILTIN_TOOL_PRICES), ...Object.keys(draft)])
    return Array.from(keys)
      .sort((a, b) => a.localeCompare(b))
      .map((key) => {
        const builtin = BUILTIN_TOOL_PRICES[key]
        const perThousand = draft[key] ?? builtin ?? 0
        return {
          key,
          perThousand,
          perCall: perThousand / 1000,
          builtin: draft[key] === undefined,
        }
      })
  }, [draft])

  const saveToolPrices = async () => {
    setSaving(true)
    try {
      const payload: Record<string, number> = {}
      Object.entries(draft).forEach(([k, v]) => {
        if (Number.isFinite(v) && v > 0) payload[k] = v
      })
      const result = await options.save(['tool_price_setting.prices'], {
        'tool_price_setting.prices': JSON.stringify(payload),
      })
      if (result) Message.success('工具附加价已保存')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="grid gap-3 lg:grid-cols-2">
      <Card
        title="表达式计费模型"
        className="!rounded-2xl"
        extra={<Tag size="small">{exprModels.length} 个</Tag>}
      >
        {exprModels.length === 0 ? (
          <Empty description="没有模型使用表达式计费，全部按倍率/固定价结算" />
        ) : (
          <div className="space-y-2">
            <Typography.Text className="text-xs text-[var(--console-text-muted)]">
              这些模型的价格由表达式决定（可在高峰/低峰、按档位计价）。**上面表格里改倍率对它们无效**，要改请编辑
              `billing_setting.billing_expr`。
            </Typography.Text>
            <Table
              size="small"
              borderCell
              pagination={false}
              rowKey="model"
              data={exprModels}
              columns={[
                { title: '模型', dataIndex: 'model', width: 200 },
                {
                  title: '表达式',
                  render: (_: unknown, row: { expr: string }) =>
                    row.expr ? (
                      <Tooltip content={row.expr}>
                        <code className="line-clamp-2 break-all text-xs text-[var(--console-text-muted)]">
                          {row.expr}
                        </code>
                      </Tooltip>
                    ) : (
                      <span className="text-xs text-slate-400">-</span>
                    ),
                },
              ]}
            />
          </div>
        )}
      </Card>

      <Card title="工具调用附加费" className="!rounded-2xl" extra={<Tag size="small">$/千次</Tag>}>
        <div className="space-y-2">
          <Typography.Text className="text-xs text-[var(--console-text-muted)]">
            联网搜索、文件检索、图片生成这类内置工具按 **调用次数** 额外计费，和 token
            价格无关。留空表示沿用内置默认；`工具名:模型前缀*` 可给特定模型单独定价。
          </Typography.Text>
          <Space>
            <Button size="mini" type="primary" loading={saving} onClick={saveToolPrices}>
              保存工具价
            </Button>
            <Button size="mini" onClick={() => setDraft({ ...BUILTIN_TOOL_PRICES })}>
              填入内置默认
            </Button>
          </Space>
          <Table
            size="small"
            borderCell
            pagination={false}
            rowKey="key"
            data={toolRows}
            columns={[
              { title: '工具 / 模型前缀', dataIndex: 'key', width: 220 },
              {
                title: '$/千次（可改）',
                width: 150,
                render: (_: unknown, row: ToolPriceRow) => (
                  <InputNumber
                    min={0}
                    step={0.5}
                    value={draft[row.key] ?? row.perThousand}
                    onChange={(v) =>
                      setDraft((prev) => {
                        const next = { ...prev }
                        const numeric = Number(v ?? 0)
                        if (!Number.isFinite(numeric) || numeric <= 0) delete next[row.key]
                        else next[row.key] = numeric
                        return next
                      })
                    }
                    style={{ width: '100%' }}
                  />
                ),
              },
              {
                title: '$/次',
                width: 110,
                render: (_: unknown, row: ToolPriceRow) => (
                  <span className="tabular-nums">${row.perCall.toFixed(5)}</span>
                ),
              },
              {
                title: '来源',
                width: 90,
                render: (_: unknown, row: ToolPriceRow) =>
                  row.builtin ? (
                    <Pill variant="info">内置默认</Pill>
                  ) : (
                    <Pill variant="success">已配置</Pill>
                  ),
              },
            ]}
          />
        </div>
      </Card>
    </div>
  )
}
